// =====================================================================================
// INVRT Radar - web console - the TopLeadsPanel (story C6, gap G3; global since O1)
//
// CHOICE (stated per the story): a panel component, mounted on the console home/chat page
// (web/src/pages/ChatPage.tsx, story O1). The panel reads GET /contacts?sort=engagement
// through useTopLeads and renders the ranked list in the order the server answers. The
// ranking itself lives server-side (RULED: engagement facts only - visits, then recency);
// this surface never re-ranks, never scores, and shows the facts as the read carries them.
//
// HONESTY (cert finding C6-F1, closed by O1): the server marks every ranked answer. Only
// ranking:"global" - the whole organisation's top N, computed server-side - may be titled
// "Top leads", and the subtitle states the bound. ranking:"recent_page" (or an older API
// with no marker: the ranking RPC not applied yet) keeps the honest recent-page copy.
// =====================================================================================

import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatDate, pluralise } from "@/lib/format";
import type { Contact } from "@/lib/types.c1";
import { contactDisplayName, TOP_LEADS_LIMIT, useTopLeads } from "@/pages/contacts/useContacts";

/** One ranked row: rank, who, where, the visit count and the last activity. */
function TopLeadRow({ contact, rank }: { contact: Contact; rank: number }) {
  return (
    <TableRow>
      <TableCell className="text-ink-muted">{rank}</TableCell>
      <TableCell className="font-medium">{contactDisplayName(contact)}</TableCell>
      <TableCell className="text-ink-muted">{contact.company.name?.trim() || "-"}</TableCell>
      <TableCell>
        {contact.engagement
          ? pluralise(contact.engagement.visit_count, "visit")
          : "no visits yet"}
      </TableCell>
      <TableCell className="text-ink-muted">
        {formatDate(contact.engagement?.last_activity_at ?? null)}
      </TableCell>
    </TableRow>
  );
}

/**
 * The ranked list (C6, global since O1): the server ranks, this panel only renders it.
 * `limit` is how deep the ranking goes (the server caps it at 50); `title`, `subtitle` and
 * `className` let a host fold the panel into its own surface without the panel learning
 * anything about the host. Without overrides the copy follows the server's own marker:
 * "Top leads" over ranking:"global", the honest recent-page copy otherwise.
 */
export function TopLeadsPanel({
  limit = TOP_LEADS_LIMIT,
  title,
  subtitle,
  className,
}: {
  /** How deep the ranking goes: the server's bounded top N for this read. */
  limit?: number;
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  const { data, error, isPending, refetch } = useTopLeads(limit);
  const contacts = data?.contacts ?? [];
  const isGlobal = data?.ranking === "global";
  const heading = title ?? (isGlobal ? "Top leads" : "Most engaged recent leads");
  const sub = subtitle ??
    (isGlobal
      ? `The top ${limit} identified contacts across your whole organisation, ranked by visits, then last activity.`
      : "Your most recent contacts, ranked by visits and then last activity.");

  return (
    <section aria-labelledby="top-leads-heading" className={className}>
      <div className="mb-2 flex flex-col gap-1">
        <h3 id="top-leads-heading" className="text-sm font-semibold text-ink">
          {heading}
        </h3>
        <p className="text-xs text-ink-muted">{sub}</p>
      </div>
      {isPending ? (
        <LoadingState label="Ranking contacts…" />
      ) : error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : contacts.length === 0 ? (
        <EmptyState
          title="No leads to rank yet"
          body="When visitors arrive, the busiest and most recently active contacts surface here."
        />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell className="w-10">#</TableHeaderCell>
              <TableHeaderCell>Contact</TableHeaderCell>
              <TableHeaderCell>Company</TableHeaderCell>
              <TableHeaderCell>Visits</TableHeaderCell>
              <TableHeaderCell>Last activity</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact, index) => (
              <TopLeadRow key={contact.id} contact={contact} rank={index + 1} />
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
